import React from "react";
import "./CatalogFilters.css";

export function CatalogFilters({ onClear, children }) {
  return (
    <aside className="als-filters">
      <div className="als-filters-head">
        <h3>Filters</h3>
        <button type="button" className="als-filters-clear" onClick={onClear}>
          Clear all
        </button>
      </div>
      {children}
    </aside>
  );
}

export function FilterGroup({ title, children }) {
  return (
    <div className="als-filter-group">
      <h4>{title}</h4>
      {children}
    </div>
  );
}

export function FilterCheckbox({ label, count, checked, onChange }) {
  return (
    <label className="als-filter-check">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span>
        {label}
        {count != null ? (
          <span className="als-filter-count"> ({count})</span>
        ) : null}
      </span>
    </label>
  );
}

/** Internshala-style pay bands with optional View More. */
export function FilterPayRanges({
  title,
  options = [],
  selected = [],
  onToggle,
  initialVisible = 4,
}) {
  return (
    <FilterOptionList
      title={title}
      options={options}
      selected={selected}
      onToggle={onToggle}
      initialVisible={initialVisible}
      getKey={(opt) => opt.key}
    />
  );
}

/** Checkbox list with counts + View More (Work mode, Location, etc.). */
export function FilterOptionList({
  title,
  options = [],
  selected = [],
  onToggle,
  initialVisible = 4,
  getKey = (opt) => opt.key ?? opt.value ?? opt.label,
}) {
  const [expanded, setExpanded] = React.useState(false);
  if (!options.length) return null;

  const visible = expanded ? options : options.slice(0, initialVisible);
  const canToggle = options.length > initialVisible;

  return (
    <FilterGroup title={title}>
      {visible.map((opt) => {
        const key = getKey(opt);
        return (
          <FilterCheckbox
            key={key}
            label={opt.label}
            count={opt.count}
            checked={selected.includes(key)}
            onChange={() => onToggle(key)}
          />
        );
      })}
      {canToggle ? (
        <button
          type="button"
          className="als-filters-more"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "View Less" : "View More"}
        </button>
      ) : null}
    </FilterGroup>
  );
}

/** Experience max slider: 0 Yrs → Any. */
export function FilterExperienceSlider({
  max = 10,
  value,
  onChange,
}) {
  const ceiling = Math.max(1, Number(max) || 10);
  const current =
    value == null || Number.isNaN(Number(value)) ? ceiling : Number(value);
  const atAny = current >= ceiling;

  return (
    <FilterGroup title="Experience">
      <div className="als-filter-price">
        <div className="als-filter-price-labels">
          <span>0 Yrs</span>
          <span className="als-filter-price-current">
            {atAny ? "Any" : `${current} Yr${current === 1 ? "" : "s"}`}
          </span>
        </div>
        <input
          type="range"
          className="als-filter-range"
          min={0}
          max={ceiling}
          step={1}
          value={Math.min(current, ceiling)}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label="Maximum experience in years"
        />
      </div>
    </FilterGroup>
  );
}

export function FilterSelect({ value, onChange, options, placeholder }) {
  return (
    <select
      className="als-filter-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

/** Single max-price slider; scale starts at 0. */
export function FilterPriceRange({
  min = 0,
  max = 0,
  value,
  onChange,
  step,
}) {
  const ceiling = Math.max(Number(max) || 0, min);
  const current =
    value == null || Number.isNaN(Number(value)) ? ceiling : Number(value);
  const resolvedStep =
    step ??
    (ceiling > 10000 ? 500 : ceiling > 1000 ? 100 : ceiling > 100 ? 10 : 1);

  return (
    <div className="als-filter-price">
      <div className="als-filter-price-labels">
        <span>₹{min.toLocaleString("en-IN")}</span>
        <span className="als-filter-price-current">
          Up to ₹{current.toLocaleString("en-IN")}
        </span>
      </div>
      <input
        type="range"
        className="als-filter-range"
        min={min}
        max={ceiling}
        step={resolvedStep}
        value={Math.min(current, ceiling)}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label="Maximum course price"
        disabled={ceiling <= min}
      />
    </div>
  );
}

export function toggleFilterValue(list, value) {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}

export const COURSE_CATEGORIES = [
  "Development",
  "Data Science",
  "Business",
  "Design",
  "Marketing",
  "IT & Software",
  "Personal Growth",
  "Other",
];

export const COURSE_LEVELS = ["Beginner", "Intermediate", "Advanced"];

export const COURSE_DURATIONS = [
  "Any Duration",
  "0-2 Weeks",
  "3-6 Months",
  "6+ Months",
];

export const COURSE_PRICES = ["Free", "Paid"];

export const COURSE_LANGUAGES = ["English"];

export const COURSE_MODES = ["Online", "Offline"];

export const COURSE_BATCH_TYPES = [
  "Morning Batch",
  "Afternoon Batch",
  "Evening Batch",
  "Weekdays Batch",
  "Weekend Batch",
  "Fast Track Batch",
];

export const COURSE_TIMEZONES = ["IST", "EST", "PST", "GMT", "CET"];

export const INTERNSHIP_CATEGORIES = [...COURSE_CATEGORIES];

export const INTERNSHIP_LOCATIONS = [
  "Remote",
  "Work From Home",
  "Bangalore",
  "Hyderabad",
  "Pune",
  "Mumbai",
  "Other",
];

export const INTERNSHIP_DURATIONS = [
  "<1 Month",
  "1-3 Months",
  "3-6 Months",
  "6+ Months",
];

export const INTERNSHIP_STIPENDS = ["Any Stipend", "Paid", "Unpaid"];

/** Annual salary bands (LPA / Lakhs) — Internshala-style. */
export const SALARY_LAKH_RANGES = [
  { key: "0-3", label: "0-3 Lakhs", min: 0, max: 3 },
  { key: "3-6", label: "3-6 Lakhs", min: 3, max: 6 },
  { key: "6-10", label: "6-10 Lakhs", min: 6, max: 10 },
  { key: "10-15", label: "10-15 Lakhs", min: 10, max: 15 },
  { key: "15-20", label: "15-20 Lakhs", min: 15, max: 20 },
  { key: "20+", label: "20+ Lakhs", min: 20, max: Infinity },
];

/** Monthly stipend bands (₹) — Naukri/Internshala style. */
export const STIPEND_MONTHLY_RANGES = [
  { key: "unpaid", label: "Unpaid", unpaid: true },
  { key: "0-10k", label: "0-10k", min: 0, max: 10000 },
  { key: "10-20k", label: "10k-20k", min: 10000, max: 20000 },
  { key: "20-30k", label: "20k-30k", min: 20000, max: 30000 },
  { key: "30k+", label: "30k+", min: 30000, max: Infinity },
];

export const EXPERIENCE_SLIDER_MAX = 10;

export const DEPARTMENT_OPTIONS = [
  "Engineering",
  "Product",
  "Design",
  "Sales",
  "Marketing",
  "HR",
  "Finance",
  "Operations",
  "Customer Success",
  "Other",
];

export const WORK_MODE_OPTIONS = ["Remote", "Hybrid", "Onsite"];

export const EMPLOYMENT_TYPE_OPTIONS = ["Full Time", "Part Time", "Contract"];

export const INTERNSHIP_TYPE_OPTIONS = ["Full Time", "Part Time"];

/** Map `companies/educations/` (education_mt) rows to qualification labels. */
export function educationQualifications(educations = []) {
  return [
    ...new Set(
      (educations || [])
        .map((item) => String(item?.qualification || "").trim())
        .filter(Boolean)
    ),
  ];
}

/** Map `companies/departments/` (department_mt) rows to department labels. */
export function departmentNames(departments = []) {
  return [
    ...new Set(
      (departments || [])
        .map((item) => String(item?.department_name || "").trim())
        .filter(Boolean)
    ),
  ].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
  );
}

export const TRAINER_SKILLS = [
  "Cloud",
  "Data Science",
  "Web Development",
  "DevOps",
  "AI / ML",
  "Soft Skills",
];

export const TRAINER_LOCATIONS = [
  "Bangalore",
  "Hyderabad",
  "Pune",
  "Mumbai",
  "Chennai",
  "Remote",
];

export const TRAINER_LEVELS = ["Beginner", "Intermediate", "Advanced"];

export const TRAINER_GOALS = [
  "Upskill",
  "Career Switch",
  "Job Ready",
  "Explore Interests",
];

export const TRAINER_SORTS = ["Most Active", "Recently Joined", "A-Z"];

export const INSTITUTE_LOCATIONS = [
  "Bangalore",
  "Hyderabad",
  "Pune",
  "Mumbai",
  "Chennai",
  "Delhi",
];

export const INSTITUTE_TYPES = [
  "Training Institute",
  "University",
  "College",
  "Online Platform",
];

export const INSTITUTE_RATINGS = ["4.0 & above", "3.0 & above", "2.0 & above"];

export const EMPLOYER_INDUSTRIES = [
  "IT Services",
  "Product",
  "Finance",
  "Healthcare",
  "Education",
  "Manufacturing",
  "Other",
];

export const EMPLOYER_SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];

export const EMPLOYER_TYPES = ["Product", "Service", "Startup"];

export const EMPLOYER_LOCATIONS = [...INSTITUTE_LOCATIONS];

const CATEGORY_KEYWORDS = {
  Development: ["dev", "web", "react", "java", "python", "coding", "program", "full stack", "frontend", "backend"],
  "Data Science": ["data", "ml", "machine", "analytics", "science", "ai", "sql"],
  Business: ["business", "mba", "management", "finance", "accounting"],
  Design: ["design", "ui", "ux", "figma", "graphic"],
  Marketing: ["market", "seo", "digital", "brand", "content"],
  "IT & Software": ["it ", "software", "cloud", "devops", "cyber", "network", "aws", "azure"],
  "Personal Growth": ["soft skill", "communication", "leadership", "career", "personality"],
  Other: [],
};

export function textMatchesCategory(text, categories) {
  if (!categories.length) return true;
  const hay = (text || "").toLowerCase();
  return categories.some((cat) => {
    if (cat === "Other") {
      const known = Object.entries(CATEGORY_KEYWORDS)
        .filter(([key]) => key !== "Other")
        .flatMap(([, words]) => words);
      return !known.some((word) => hay.includes(word));
    }
    const words = CATEGORY_KEYWORDS[cat] || [cat.toLowerCase()];
    return words.some((word) => hay.includes(word));
  });
}

export function parseDurationMonths(raw) {
  const text = String(raw || "").toLowerCase();
  if (!text) return null;
  const weekMatch = text.match(/(\d+)\s*week/);
  if (weekMatch) return Number(weekMatch[1]) / 4;
  const monthMatch = text.match(/(\d+)\s*month/);
  if (monthMatch) return Number(monthMatch[1]);
  const dayMatch = text.match(/(\d+)\s*day/);
  if (dayMatch) return Number(dayMatch[1]) / 30;
  const num = parseFloat(text);
  return Number.isFinite(num) ? num : null;
}

export function matchesCourseDuration(raw, selected) {
  if (!selected.length || selected.includes("Any Duration")) return true;
  const months = parseDurationMonths(raw);
  return selected.some((opt) => {
    if (opt === "Any Duration") return true;
    if (opt === "0-2 Weeks") return months == null || months <= 0.5;
    if (opt === "3-6 Months") return months != null && months >= 3 && months <= 6;
    if (opt === "6+ Months") return months != null && months > 6;
    return true;
  });
}

export function matchesInternshipDuration(raw, selected) {
  if (!selected.length) return true;
  const months = parseDurationMonths(raw);
  return selected.some((opt) => {
    if (opt === "<1 Month") return months == null || months < 1;
    if (opt === "1-3 Months") return months != null && months >= 1 && months <= 3;
    if (opt === "3-6 Months") return months != null && months > 3 && months <= 6;
    if (opt === "6+ Months") return months != null && months > 6;
    return true;
  });
}

export function isPaidStipend(stipend) {
  const text = String(stipend || "").toLowerCase();
  if (!text || text.includes("unpaid") || text === "0" || text === "na") {
    return false;
  }
  if (text.includes("paid") || /\d/.test(text)) return true;
  return false;
}

export function matchesLevel(text, levels) {
  if (!levels.length) return true;
  const hay = (text || "").toLowerCase();
  return levels.some((level) => hay.includes(level.toLowerCase()));
}

export function isCourseFree(fee) {
  const value = Number(fee);
  return !Number.isFinite(value) || value === 0;
}

export function getCourseFeeValue(fee) {
  const value = Number(fee);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function matchesExactValues(value, selected) {
  if (!selected.length) return true;
  const current = String(value || "").trim();
  if (!current) return false;
  return selected.some(
    (item) => item.toLowerCase() === current.toLowerCase()
  );
}

export function matchesAnyExactValues(values, selected) {
  if (!selected.length) return true;
  const list = (values || []).map((v) => String(v || "").trim().toLowerCase());
  return selected.some((item) => list.includes(item.toLowerCase()));
}

/** Legacy Free/Paid checkbox match (kept for older callers). */
export function matchesCoursePrice(fee, selected) {
  if (!selected.length) return true;
  const free = isCourseFree(fee);
  return selected.some((price) => (price === "Free" ? free : !free));
}

/** Max-price slider: include courses with fee ≤ priceMax. Full range = no filter. */
export function matchesCoursePriceMax(fee, priceMax, priceCeiling) {
  if (priceMax == null || priceCeiling == null) return true;
  if (priceMax >= priceCeiling) return true;
  return getCourseFeeValue(fee) <= priceMax;
}

export function matchesStipendOptions(stipend, selected) {
  if (!selected.length) return true;
  const paid = isPaidStipend(stipend);
  return selected.some((opt) => (opt === "Paid" ? paid : !paid));
}

/** Parse free-text salary into annual lakhs (LPA). */
export function parseSalaryLakhs(text) {
  const s = String(text || "")
    .toLowerCase()
    .replace(/,/g, "")
    .trim();
  if (!s || /unpaid|not specified|not disclosed|n\/a|\bna\b|competitive/.test(s)) {
    return null;
  }

  const rangeLakh = s.match(
    /(\d+(?:\.\d+)?)\s*(?:-|–|to)\s*(\d+(?:\.\d+)?)\s*(?:lpa|lakhs?)/
  );
  if (rangeLakh) {
    return (Number(rangeLakh[1]) + Number(rangeLakh[2])) / 2;
  }

  const singleLakh = s.match(/(\d+(?:\.\d+)?)\s*\+?\s*(?:lpa|lakhs?)/);
  if (singleLakh) return Number(singleLakh[1]);

  const nums = [...s.matchAll(/\d+(?:\.\d+)?/g)].map((m) => Number(m[0]));
  if (!nums.length) return null;
  const n = Math.max(...nums);
  if (n >= 100000) return n / 100000;
  if (n >= 1000) return (n * 12) / 100000;
  return n;
}

/** Parse free-text stipend into monthly rupees. */
export function parseStipendMonthly(text) {
  const s = String(text || "")
    .toLowerCase()
    .replace(/,/g, "")
    .trim();
  if (!s || /unpaid|not disclosed|n\/a|\bna\b/.test(s)) return 0;

  const rangeLakh = s.match(
    /(\d+(?:\.\d+)?)\s*(?:-|–|to)\s*(\d+(?:\.\d+)?)\s*(?:lpa|lakhs?)/
  );
  if (rangeLakh) {
    return (((Number(rangeLakh[1]) + Number(rangeLakh[2])) / 2) * 100000) / 12;
  }

  const singleLakh = s.match(/(\d+(?:\.\d+)?)\s*\+?\s*(?:lpa|lakhs?)/);
  if (singleLakh) return (Number(singleLakh[1]) * 100000) / 12;

  const nums = [...s.matchAll(/\d+(?:\.\d+)?/g)].map((m) => Number(m[0]));
  if (!nums.length) return null;
  const n = Math.max(...nums);
  if (n >= 100000) return n / 12;
  return n;
}

function valueInRange(value, min, max) {
  if (value == null || Number.isNaN(value)) return false;
  if (max === Infinity) return value >= min;
  return value >= min && value < max;
}

export function matchesSalaryRanges(salary, selectedKeys) {
  if (!selectedKeys.length) return true;
  const lakhs = parseSalaryLakhs(salary);
  if (lakhs == null) return false;
  return selectedKeys.some((key) => {
    const range = SALARY_LAKH_RANGES.find((r) => r.key === key);
    return range ? valueInRange(lakhs, range.min, range.max) : false;
  });
}

export function matchesStipendRanges(stipend, selectedKeys) {
  if (!selectedKeys.length) return true;
  const paid = isPaidStipend(stipend);
  const monthly = parseStipendMonthly(stipend);
  return selectedKeys.some((key) => {
    const range = STIPEND_MONTHLY_RANGES.find((r) => r.key === key);
    if (!range) return false;
    if (range.unpaid) return !paid;
    if (!paid || monthly == null) return false;
    return valueInRange(monthly, range.min, range.max);
  });
}

/** Parse free-text experience into years (fresher → 0). */
export function parseExperienceYears(text) {
  const s = String(text || "")
    .toLowerCase()
    .replace(/,/g, "")
    .trim();
  if (!s) return null;
  if (/fresher|entry|no experience|0\s*-\s*0/.test(s)) return 0;

  const range = s.match(/(\d+(?:\.\d+)?)\s*(?:-|–|to)\s*(\d+(?:\.\d+)?)/);
  if (range) return Number(range[2]);

  const plus = s.match(/(\d+(?:\.\d+)?)\s*\+/);
  if (plus) return Number(plus[1]);

  const single = s.match(/(\d+(?:\.\d+)?)\s*(?:yr|year)?/);
  if (single) return Number(single[1]);
  return null;
}

export function matchesExperienceMax(experience, maxYears, ceiling) {
  if (maxYears == null || ceiling == null) return true;
  if (maxYears >= ceiling) return true;
  const years = parseExperienceYears(experience);
  if (years == null) return false;
  return years <= maxYears;
}

function buildCountedOptions(items, getValue) {
  const counts = new Map();
  (items || []).forEach((item) => {
    const value = String(getValue(item) || "").trim();
    if (!value) return;
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return [...counts.entries()]
    .sort((a, b) =>
      a[0].localeCompare(b[0], undefined, { sensitivity: "base", numeric: true })
    )
    .map(([value, count]) => ({
      key: value,
      label: value,
      count,
    }));
}

/** Always show fixed choices; count how many listings match each. */
function buildFixedOptions(presetValues, items, getValue) {
  const counts = new Map();
  (items || []).forEach((item) => {
    const value = String(getValue(item) || "").trim();
    if (!value) return;
    counts.set(value.toLowerCase(), (counts.get(value.toLowerCase()) || 0) + 1);
  });

  return presetValues.map((value) => ({
    key: value,
    label: value,
    count: counts.get(value.toLowerCase()) || 0,
  }));
}

function getCompanyType(item) {
  const bt = item?.company?.business_type;
  if (!bt) return "";
  if (typeof bt === "string") return bt;
  return bt.business_type || "";
}

function buildRangeOptions(ranges, items, getValue, matchOne) {
  return ranges.map((range) => {
    const count = (items || []).filter((item) =>
      matchOne(getValue(item), range)
    ).length;
    return { ...range, count };
  });
}

function matchSalaryRangeValue(salary, range) {
  const lakhs = parseSalaryLakhs(salary);
  if (lakhs == null) return false;
  return valueInRange(lakhs, range.min, range.max);
}

function matchStipendRangeValue(stipend, range) {
  const paid = isPaidStipend(stipend);
  if (range.unpaid) return !paid;
  if (!paid) return false;
  const monthly = parseStipendMonthly(stipend);
  if (monthly == null) return false;
  return valueInRange(monthly, range.min, range.max);
}

function uniqueSorted(values = []) {
  return [
    ...new Set(
      values
        .map((v) => String(v || "").trim())
        .filter(Boolean)
    ),
  ].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
  );
}

/** Build filter options from published batches currently in the app. */
export function buildCourseFilterOptions(courses = []) {
  const modes = [];
  const timezones = [];
  const batchTypes = [];
  const durations = [];
  const courseNames = [];
  let maxFee = 0;

  courses.forEach((course) => {
    if (course?.mode) modes.push(course.mode);
    if (course?.timezone) timezones.push(course.timezone);
    if (course?.batch_name) batchTypes.push(course.batch_name);
    if (course?.duration) durations.push(course.duration);

    const courseName = course?.course_material?.course?.course_name;
    if (courseName) courseNames.push(courseName);

    const fee = getCourseFeeValue(course?.fee);
    if (fee > maxFee) maxFee = fee;
  });

  // Slider always goes to at least ₹10,000; higher if any course costs more.
  const rounded =
    maxFee <= 0
      ? 10000
      : maxFee <= 10000
        ? 10000
        : Math.ceil(maxFee / 100) * 100;

  return {
    courseNames: uniqueSorted(courseNames),
    modes: uniqueSorted(modes),
    batchTypes: uniqueSorted(batchTypes),
    timezones: uniqueSorted(timezones),
    durations: uniqueSorted(durations),
    priceCeiling: rounded,
  };
}

/** Internship filters from posted internship fields. */
export function buildInternshipFilterOptions(
  internships = [],
  { educationOptions = [], departmentOptions = [] } = {}
) {
  const educationPresets = educationQualifications(educationOptions);
  const departmentPresets = departmentNames(departmentOptions);
  return {
    roles: buildCountedOptions(internships, (item) => item?.role),
    workModes: buildFixedOptions(
      WORK_MODE_OPTIONS,
      internships,
      (item) => item?.work_mode
    ),
    types: buildFixedOptions(
      INTERNSHIP_TYPE_OPTIONS,
      internships,
      (item) => item?.internship_type
    ),
    locations: buildCountedOptions(internships, (item) => item?.location),
    durations: buildCountedOptions(internships, (item) => item?.duration),
    companyTypes: buildCountedOptions(internships, getCompanyType),
    departments: departmentPresets.length
      ? buildFixedOptions(
          departmentPresets,
          internships,
          (item) => item?.department
        )
      : buildFixedOptions(
          DEPARTMENT_OPTIONS,
          internships,
          (item) => item?.department
        ),
    educations: educationPresets.length
      ? buildFixedOptions(
          educationPresets,
          internships,
          (item) => item?.education
        )
      : buildCountedOptions(internships, (item) => item?.education),
    stipends: buildRangeOptions(
      STIPEND_MONTHLY_RANGES,
      internships,
      (item) => item?.stipend,
      matchStipendRangeValue
    ),
  };
}

/** Job filters from posted job fields. */
export function buildJobFilterOptions(
  jobs = [],
  { educationOptions = [], departmentOptions = [] } = {}
) {
  let maxYears = 0;
  (jobs || []).forEach((job) => {
    const years = parseExperienceYears(job?.experience);
    if (years != null && years > maxYears) maxYears = years;
  });

  const educationPresets = educationQualifications(educationOptions);
  const departmentPresets = departmentNames(departmentOptions);

  return {
    roles: buildCountedOptions(jobs, (item) => item?.role),
    workModes: buildFixedOptions(
      WORK_MODE_OPTIONS,
      jobs,
      (item) => item?.work_mode
    ),
    types: buildFixedOptions(
      EMPLOYMENT_TYPE_OPTIONS,
      jobs,
      (item) => item?.employment_type
    ),
    locations: buildCountedOptions(jobs, (item) => item?.location),
    companyTypes: buildCountedOptions(jobs, getCompanyType),
    departments: departmentPresets.length
      ? buildFixedOptions(departmentPresets, jobs, (item) => item?.department)
      : buildFixedOptions(DEPARTMENT_OPTIONS, jobs, (item) => item?.department),
    educations: educationPresets.length
      ? buildFixedOptions(educationPresets, jobs, (item) => item?.education)
      : buildCountedOptions(jobs, (item) => item?.education),
    experienceCeiling: Math.max(EXPERIENCE_SLIDER_MAX, Math.ceil(maxYears) || 0),
    salaries: buildRangeOptions(
      SALARY_LAKH_RANGES,
      jobs,
      (item) => item?.salary,
      matchSalaryRangeValue
    ),
  };
}

/** Employer / institute filters from company list (+ optional city). */
export function buildCompanyFilterOptions(companies = []) {
  const businessTypes = [];
  const cities = [];

  companies.forEach((company) => {
    const type = company?.business_type?.business_type;
    if (type) businessTypes.push(type);
    if (company?.city) cities.push(company.city);
  });

  return {
    businessTypes: uniqueSorted(businessTypes),
    cities: uniqueSorted(cities),
  };
}

/** Trainer filters from trainer profiles (+ optional courses/city). */
export function buildTrainerFilterOptions(trainers = []) {
  const genders = [];
  const statuses = [];
  const courses = [];
  const cities = [];

  trainers.forEach((trainer) => {
    if (trainer?.gender) genders.push(trainer.gender);
    if (trainer?.status) statuses.push(trainer.status);
    if (trainer?.city) cities.push(trainer.city);
    (trainer?.courseNames || []).forEach((name) => courses.push(name));
  });

  return {
    genders: uniqueSorted(genders),
    statuses: uniqueSorted(statuses),
    courses: uniqueSorted(courses),
    cities: uniqueSorted(cities),
  };
}
